/**
 * Daily Challenge reset reminder — local notification, no server needed.
 *
 * Schedules a daily 09:00 LOCAL time notification on Android that nudges
 * the user to come back and play today's board. We chose 09:00 local
 * rather than 00:00 UTC because UTC midnight lands in the middle of the
 * night for most of our users and would just annoy them; 09:00 local is
 * the breakfast-coffee window when daily-puzzle players reliably engage.
 *
 * Capacitor's LocalNotifications schedules survive app restarts and
 * device reboots on Android 14+ (we use `every: "day"` so we only need
 * one schedule call ever per install).
 *
 * Idempotent — calling ensureDailyResetReminder() multiple times just
 * re-asserts the same schedule, no duplicates.
 */
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";

const DAILY_NOTIFICATION_ID = 1001;
const NOTIFY_HOUR_LOCAL = 9;  // 09:00 in the user's local timezone

function isAndroidNative() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

export async function ensureDailyResetReminder({ title, body, skipToday = false } = {}) {
  if (!isAndroidNative()) return { skipped: "non-native" };
  try {
    // Permissions — Android 13+ requires POST_NOTIFICATIONS at runtime.
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== "granted") {
      const req = await LocalNotifications.requestPermissions();
      if (req.display !== "granted") return { skipped: "permission-denied" };
    }

    // Android 8.0+ silently drops notifications without a registered
    // channel — the previous schedule referenced channelId
    // "fitjong_daily" but never created it, so the user never got the
    // 09:00 reminder (tester complaint v36: "I was not notified
    // regarding daily challenge"). createChannel is idempotent.
    try {
      await LocalNotifications.createChannel({
        id: "fitjong_daily",
        name: "Daily Challenge",
        description: "Reminders to play today's challenge",
        importance: 4, // HIGH — pops the heads-up banner
        visibility: 1,
        lights: true,
        vibration: true,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[notifications] createChannel failed:", e);
    }

    // Cancel any prior schedule with the same id so the new schedule wins
    // (e.g. if NOTIFY_HOUR_LOCAL changes in a future release).
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIFICATION_ID }] }).catch(() => {});

    // First fire: today at 09:00 if not yet passed AND skipToday===false,
    // otherwise tomorrow. Tester complaint iter54: "I have done daily
    // challenge so the notification shouldn't pop up after." Passing
    // skipToday=true pushes the first fire to TOMORROW 09:00 — the daily
    // schedule then continues normally from there.
    const at = new Date();
    at.setHours(NOTIFY_HOUR_LOCAL, 0, 0, 0);
    if (skipToday || at.getTime() <= Date.now()) {
      at.setDate(at.getDate() + 1);
    }

    await LocalNotifications.schedule({
      notifications: [{
        id: DAILY_NOTIFICATION_ID,
        title: title || "Fitjong",
        body: body || "Today's Daily Challenge is live — keep your streak going!",
        schedule: {
          at,
          allowWhileIdle: true,
          repeats: true,
          every: "day",
        },
        // No `smallIcon` override — Capacitor falls back to the app's
        // launcher icon when omitted. Pointing at a drawable that
        // doesn't exist in the AAB used to silently drop the post.
        channelId: "fitjong_daily",
      }],
    });

    return { scheduled: at.toISOString() };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[notifications] daily reset schedule failed:", e);
    return { skipped: "error", error: String(e?.message || e) };
  }
}

export async function cancelDailyResetReminder() {
  if (!isAndroidNative()) return;
  try {
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIFICATION_ID }] });
  } catch {}
}
